import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import * as FlatStates from '../../internal';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';
import { patch } from "@ngxs/store/operators";

export const QUESTION_ENTITY_TOKEN = new StateToken<QuestionEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.QUESTION_ENTITY);

export interface QuestionEntityStateModel extends FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.QuestionEntityModels.QuestionEntityType> {
  questionId: string | null;
}

@State<QuestionEntityStateModel>({
  name: QUESTION_ENTITY_TOKEN,
  defaults: {
    questionId: null,
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class QuestionEntityState extends HistoryEntityBaseState<FlatModels.QuestionEntityModels.QuestionEntityType, QuestionEntityStateModel> {
  constructor(public store: Store) {
    super();
  }

  //<editor-fold desc="Selectors">
  
  @Selector([QuestionEntityState])
  static questionId$(state: QuestionEntityStateModel): string | null {
    return state.questionId;
  }

  @Selector([QuestionEntityState])
  static override typedEntities$(state: QuestionEntityStateModel): { [id: string]: FlatModels.QuestionEntityModels.QuestionEntityType } {
    return HistoryEntityBaseState.typedEntities$(state);
  }
	
  @Selector([QuestionEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.QuestionEntityModels.QuestionEntityType }): FlatModels.QuestionEntityModels.QuestionEntityType[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }
	
  @Selector([QuestionEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.QuestionEntityModels.QuestionEntityType }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }
	
  @Selector([QuestionEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.QuestionEntityModels.QuestionEntityType }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }
  
  @Selector([
    QuestionEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedSlidePage$
  ])
  static currentSelectedTypedSlideQuestions$(typedEntities: { [id: string]: FlatModels.QuestionEntityModels.QuestionEntityType },
                                             currentSelectedSlidePage: FlatModels.PageEntityModels.Slide | null) {
    if (currentSelectedSlidePage && currentSelectedSlidePage.questions) {
      return currentSelectedSlidePage.questions.reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr[entity.id] = entity;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.QuestionEntityModels.QuestionEntityType });
    }
    return {};
  }
  
  @Selector([QuestionEntityState.currentSelectedTypedSlideQuestions$])
  static currentSelectedSlideQuestions$(currentSelectedTypedSlideQuestions: { [id: string]: FlatModels.QuestionEntityModels.QuestionEntityType }) {
    return Object.values(currentSelectedTypedSlideQuestions);
  }
  
  @Selector([
    QuestionEntityState.currentSelectedTypedSlideQuestions$,
    QuestionEntityState.questionId$
  ])
  static currentSelectedSlideQuestion$(currentSelectedTypedSlideQuestions: { [id: string]: FlatModels.QuestionEntityModels.QuestionEntityType },
                                       questionId: string | null) {
    return questionId && currentSelectedTypedSlideQuestions[questionId] || null;
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.QuestionEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<QuestionEntityStateModel>,
                            action: FlatActions.QuestionEntityActions.GetEntitySuccess) {
    super.getEntitySuccess(state, action);
    this.store.dispatch(new FlatActions.TrainingSharedActions.SetupQuestionChoicesOrder(action.entityQueryResult.entity as FlatModels.QuestionEntityModels.QuestionEntityType));
  }

  @Action(FlatActions.QuestionEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<QuestionEntityStateModel>,
                              action: FlatActions.QuestionEntityActions.GetEntitiesSuccess) {
    super.getEntitiesSuccess(state, action);
    for (const entityQueryResult of action.entityQueryResults) {
      this.store.dispatch(new FlatActions.TrainingSharedActions.SetupQuestionChoicesOrder(entityQueryResult.entity as FlatModels.QuestionEntityModels.QuestionEntityType));
    }
  }

  @Action(FlatActions.QuestionEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<QuestionEntityStateModel>,
                               action: FlatActions.QuestionEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.QuestionEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<QuestionEntityStateModel>,
                                 action: FlatActions.QuestionEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.QuestionEntityActions.AddEntity)
  @Undoable(
    FlatActions.QuestionEntityActions.AddEntity,
    QUESTION_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<QuestionEntityStateModel>,
                     action: FlatActions.QuestionEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }
	
  @Action(FlatActions.QuestionEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.QuestionEntityActions.UpdateEntity,
    QUESTION_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<QuestionEntityStateModel>,
                        action: FlatActions.QuestionEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }
	
  @Action(FlatActions.QuestionEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.QuestionEntityActions.RemoveEntity,
    QUESTION_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<QuestionEntityStateModel>,
                        action: FlatActions.QuestionEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">
	
  @Action(FlatActions.QuestionEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<QuestionEntityStateModel>,
                        action: FlatActions.QuestionEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }
	
  @Action(FlatActions.QuestionEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<QuestionEntityStateModel>,
                        action: FlatActions.QuestionEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }
	
  @Action(FlatActions.QuestionEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<QuestionEntityStateModel>,
                            action: FlatActions.QuestionEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>
  
  @Action(FlatActions.QuestionEntityActions.SetQuestionId)
  setPoiId({setState}: StateContext<QuestionEntityStateModel>,
           {questionId}: FlatActions.QuestionEntityActions.SetQuestionId) {
    setState(
      patch({
        questionId
      })
    );
  }
	
  @Action(FlatActions.QuestionEntityActions.RearrangeQuestions)
  rearrangeQuestions({getState, dispatch}: StateContext<QuestionEntityStateModel>,
                     {questions}: FlatActions.QuestionEntityActions.RearrangeQuestions) {
    // Get Entities
    const entities = structuredClone(getState().entities);

    // Iterate Through Questions
    for (let i = 0; i < questions.length; i++) {
      // Get Question
      const question = questions[i];

      // Get Entity
      let entity;
      if (typeof question === 'string') {
        entity = entities[question];
      } else {
        entity = entities[question.id];
      }

      // Update Entity
      entity.order = i;

      // Dispatch Update
      dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(entity));
    }
  }

  //</editor-fold>
}
